import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Sparkles, TrendingUp, TrendingDown, Minus, Calendar, 
  FileText, RefreshCw, ChevronRight, BarChart3, AlertTriangle,
  CheckCircle, Clock, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Simple markdown renderer for AI reports
const renderMarkdown = (text) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements = [];
  let currentList = [];
  let listKey = 0;
  
  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${listKey++}`} className="list-disc list-inside space-y-1 my-2 text-slate-600 ml-2">
          {currentList}
        </ul>
      );
      currentList = [];
    }
  };
  
  const formatInlineText = (text) => {
    // Handle **bold** text
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-slate-800">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };
  
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed) {
      flushList();
      return;
    }
    
    // Main title (## Title)
    if (trimmed.startsWith('## ')) {
      flushList();
      const headerText = trimmed.replace(/^##\s*/, '');
      elements.push(
        <h3 key={`h2-${idx}`} className="text-base font-bold text-slate-800 mt-3 mb-2 pb-1 border-b border-purple-100">
          {headerText}
        </h3>
      );
      return;
    }
    
    // Section headers (### 1) Title or ### Title)
    if (trimmed.startsWith('### ')) {
      flushList();
      const headerText = trimmed.replace(/^###\s*\d*\)?\s*/, '');
      elements.push(
        <h4 key={`h-${idx}`} className="text-sm font-semibold text-purple-700 mt-4 mb-2 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
          {headerText}
        </h4>
      );
      return;
    }
    
    // Week/Date line (Week: ...)
    if (trimmed.startsWith('Week:') || trimmed.startsWith('**Week:')) {
      flushList();
      elements.push(
        <p key={`week-${idx}`} className="text-xs text-slate-500 mb-3">
          {formatInlineText(trimmed.replace(/^\*\*Week:\*\*\s*/, 'Week: '))}
        </p>
      );
      return;
    }
    
    // Bullet points (- item or • item or numbered 1. item)
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || /^\d+\.\s/.test(trimmed)) {
      const content = trimmed.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '');
      currentList.push(
        <li key={`li-${idx}`} className="text-xs leading-relaxed text-slate-600">
          {formatInlineText(content)}
        </li>
      );
      return;
    }
    
    // Regular paragraph
    flushList();
    elements.push(
      <p key={`p-${idx}`} className="text-xs text-slate-600 leading-relaxed my-2">
        {formatInlineText(trimmed)}
      </p>
    );
  });
  
  flushList();
  return elements;
};

const AIWeeklyReportWidget = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getWeeklyReport();
      if (res.data.success) {
        setReport(res.data.report);
        setHasLoaded(true);
      } else {
        setError(res.data.error || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Failed to fetch weekly report:', err);
      setError('Unable to load AI report');
    } finally {
      setLoading(false);
    }
  };

  // Don't auto-load - wait for user to click button
  // useEffect(() => {
  //   fetchReport();
  // }, []);

  const copyToClipboard = () => {
    if (!report) return;
    
    const { stats, narrative, period } = report;
    
    const formattedText = `AI INSIGHTS - ${period}

STATS SUMMARY:
• Total Tickets: ${stats.total_tickets} (${stats.vs_last_week >= 0 ? '+' : ''}${stats.vs_last_week} vs last week)
• Resolved: ${stats.resolved} (${stats.resolution_rate}% resolution rate)
• Still Open: ${stats.still_open}
• Avg Resolution Time: ${stats.avg_resolution_hours} hours
${stats.sla_breaches > 0 ? `• SLA Breaches: ${stats.sla_breaches}` : ''}

TOP CATEGORIES:
${stats.top_categories?.slice(0, 5).map(cat => `• ${cat.name}: ${cat.count}`).join('\n') || 'N/A'}

AI ANALYSIS:
${narrative}`;

    navigator.clipboard.writeText(formattedText);
    toast.success('AI Insights copied to clipboard!');
  };

  const addToFanReport = () => {
    if (!report) return;
    
    // Store the AI insights in sessionStorage for the Reports page to pick up
    const { stats, narrative, period } = report;
    const aiInsights = {
      period,
      stats,
      narrative,
      timestamp: new Date().toISOString()
    };
    sessionStorage.setItem('ai_insights_for_fan', JSON.stringify(aiInsights));
    
    // Navigate to reports page
    navigate('/reports?add_ai_insights=true');
    toast.success('Opening FAN Report with AI Insights...');
  };

  const getTrendIcon = (value) => {
    if (value > 0) return <TrendingUp className="h-3 w-3 text-red-500" />;
    if (value < 0) return <TrendingDown className="h-3 w-3 text-green-500" />;
    return <Minus className="h-3 w-3 text-gray-400" />;
  };

  const getTrendColor = (value) => {
    if (value > 0) return 'text-red-600';
    if (value < 0) return 'text-green-600';
    return 'text-gray-500';
  };

  if (loading) {
    return (
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-5 w-5 animate-spin text-purple-500 mr-2" />
            <span className="text-sm text-purple-600">Generating AI insights...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show initial state with button to generate
  if (!hasLoaded && !error) {
    return (
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center py-6">
            <Sparkles className="h-8 w-8 text-purple-400 mb-3" />
            <h3 className="text-sm font-medium text-purple-700 mb-1">AI Weekly Insights</h3>
            <p className="text-xs text-slate-500 mb-4 text-center">
              Get AI-powered analysis of this week's ticket trends and patterns
            </p>
            <Button 
              onClick={fetchReport}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate AI Insights
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-orange-200 bg-orange-50/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-4 text-orange-600">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span className="text-sm">{error}</span>
          </div>
          <div className="text-center">
            <Button variant="outline" size="sm" onClick={fetchReport}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!report) return null;

  const { stats, narrative, period } = report;

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50/80 to-white overflow-hidden" data-testid="ai-weekly-report-widget">
      <CardHeader className="pb-2 bg-gradient-to-r from-purple-100/50 to-transparent">
        <CardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2 text-purple-700">
            <Sparkles className="h-4 w-4" />
            AI Weekly Insights
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-purple-100 text-purple-700 text-xs font-normal">
              <Calendar className="h-3 w-3 mr-1" />
              {period}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchReport}
              className="h-7 w-7 p-0 text-purple-500 hover:text-purple-700"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-3">
        {/* Quick Stats Grid */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg p-2.5 border border-purple-100 text-center">
            <p className="text-lg font-bold text-slate-800">{stats.total_tickets}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Tickets</p>
            <div className={`flex items-center justify-center gap-0.5 text-[10px] ${getTrendColor(stats.vs_last_week)}`}>
              {getTrendIcon(stats.vs_last_week)}
              <span>{Math.abs(stats.vs_last_week)} vs last wk</span>
            </div>
          </div>
          <div className="bg-white rounded-lg p-2.5 border border-green-100 text-center">
            <p className="text-lg font-bold text-green-600">{stats.resolved}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Resolved</p>
            <div className="flex items-center justify-center gap-0.5 text-[10px] text-green-600">
              <CheckCircle className="h-3 w-3" />
              <span>{stats.resolution_rate}%</span>
            </div>
          </div>
          <div className="bg-white rounded-lg p-2.5 border border-blue-100 text-center">
            <p className="text-lg font-bold text-blue-600">{stats.still_open}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Still Open</p>
          </div>
          <div className="bg-white rounded-lg p-2.5 border border-orange-100 text-center">
            <p className="text-lg font-bold text-orange-600">{stats.avg_resolution_hours}h</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Avg Resolve</p>
            <div className="flex items-center justify-center gap-0.5 text-[10px] text-slate-400">
              <Clock className="h-3 w-3" />
            </div>
          </div>
        </div>

        {/* Top Categories */}
        {stats.top_categories && stats.top_categories.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <BarChart3 className="h-3 w-3" />
              Top Categories
            </p>
            <div className="flex flex-wrap gap-1.5">
              {stats.top_categories.slice(0, 4).map((cat, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className="text-xs bg-white border-slate-200"
                >
                  {cat.name}: {cat.count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* AI Narrative Summary */}
        <div className="bg-white/80 rounded-lg p-3 border border-purple-100">
          <p className="text-[10px] text-purple-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <FileText className="h-3 w-3" />
            AI Analysis
          </p>
          <div className={`${!expanded ? 'max-h-24 overflow-hidden relative' : ''}`}>
            <div className="prose prose-sm max-w-none">
              {renderMarkdown(narrative)}
            </div>
            {!expanded && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/90 to-transparent"></div>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setExpanded(!expanded)}
            className="mt-2 h-6 text-xs text-purple-600 hover:text-purple-800 p-0"
          >
            {expanded ? 'Show less' : 'Read full report'}
            <ChevronRight className={`h-3 w-3 ml-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Button>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-purple-100">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={copyToClipboard}
              className="h-7 text-xs flex-1"
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={addToFanReport}
              className="h-7 text-xs flex-1 bg-purple-600 hover:bg-purple-700"
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Add to FAN Report
            </Button>
          </div>
        </div>

        {/* SLA Breaches Alert */}
        {stats.sla_breaches > 0 && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-2.5 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="text-xs text-red-700">
              <strong>{stats.sla_breaches}</strong> SLA breaches this week
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIWeeklyReportWidget;
